import { TableFilterState, TableSortingState } from '@ibsys/kit';
import type {
  SearchConversationRequest,
  SearchConversationPersonFilter,
  SearchConversationExpertReportFilter,
  SearchConversationExpertReportBlockFilter,
  SearchDialogsPage,
  ConversationSort,
  ConversationExpertReportSort,
  SortDirection,
  SortField,
} from '@/entities/search';
import { tableFilters2Query } from '@/shared/lib';

/** Коды колонок участников: и «Клиент», и «Консультант» приходят из объединённого состава `persons` */
const PERSONS_COLUMN_ALIASES = ['client', 'employeeId'] as const;

const EXPERT_REPORT_PREFIX = 'expertReport_';

type ExpertReportFilterValue =
  | { from?: number; to?: number }
  | { from?: string; to?: string }
  | string
  | string[]
  | undefined;

/** Статус оценки — значение фильтра колонки «Статус» экспертного заключения */
type ExpertReportStatus = NonNullable<SearchConversationExpertReportFilter['status']>;

const EXPERT_REPORT_STATUSES: readonly ExpertReportStatus[] = ['NEW', 'IN_PROCESS', 'DONE', 'ERROR'];

/**
 * Приводит значение фильтра статуса к списку валидных статусов.
 * Фильтр `multiselect` отдаёт массив в режиме «Несколько»,
 * поэтому поддерживаются оба формата.
 */
const toStatusList = (value: unknown): ExpertReportStatus[] => {
  const raw = Array.isArray(value) ? value : [value];
  return raw.filter((item): item is ExpertReportStatus =>
    typeof item === 'string' && (EXPERT_REPORT_STATUSES as readonly string[]).includes(item),
  );
};

const isRatingRange = (v: unknown): v is { from?: number; to?: number } =>
  typeof v === 'object' && v !== null && ('from' in v || 'to' in v);

/**
 * Значения справочника из фильтра колонки-значения. Фильтр `multiselect` отдаёт набор массивом,
 * но в сохранённых настройках таблицы мог остаться и скаляр от прежнего режима «Один».
 */
const toValueList = (v: unknown): string[] => {
  const raw = Array.isArray(v) ? v : [v];
  return raw.filter((item): item is string => typeof item === 'string' && item.length > 0);
};

/**
 * Значение отбора по резюме: строка уходит в запрос как есть, наравне со значением отбора любой
 * текстовой колонки. Строка из одних пробелов условия не ставит — искать в резюме нечего.
 */
const toSummaryFilter = (v: unknown): string => (typeof v === 'string' && v.trim() ? v : '');

const isDateRange = (v: unknown): v is { from?: string; to?: string } =>
  typeof v === 'object' && v !== null && ('from' in v || 'to' in v);

type ExpertReportFieldFilter = 'rating' | 'summary' | 'startedAt' | 'finishedAt' | 'status';
type ExpertReportFilterParseResult =
  | { reportId: number; type: 'field'; field: ExpertReportFieldFilter }
  | { reportId: number; type: 'block'; blockId: number };

/**
 * Парсит ID фильтра экспертного заключения.
 * Поддерживаемые форматы:
 *  - `expertReport_{reportId}` — рейтинг отчёта (для обратной совместимости)
 *  - `expertReport_{reportId}_{field}` — стандартное поле (rating/summary/startedAt/finishedAt/status)
 *  - `expertReport_{reportId}_block_{blockId}` — балл за блок
 */
const parseExpertReportFilterId = (id: string): ExpertReportFilterParseResult | null => {
  if (!id.startsWith(EXPERT_REPORT_PREFIX)) return null;
  const suffix = id.slice(EXPERT_REPORT_PREFIX.length);
  const parts = suffix.split('_');
  const reportId = parseInt(parts[0], 10);
  if (Number.isNaN(reportId)) return null;
  if (parts.length === 1) return { reportId, type: 'field', field: 'rating' };
  if (parts[1] === 'block' && parts.length >= 3) {
    const blockId = parseInt(parts[2], 10);
    if (Number.isNaN(blockId)) return null;
    return { reportId, type: 'block', blockId };
  }
  if (['rating', 'summary', 'startedAt', 'finishedAt', 'status'].includes(parts[1])) {
    return { reportId, type: 'field', field: parts[1] as ExpertReportFieldFilter };
  }
  return null;
};

/**
 * Builds conversation2expertReport array from table filters (spec: api/search/query.md).
 * Filter ids:
 *  - expertReport_{reportId}_{field} — стандартные поля (rating/summary/startedAt/finishedAt/status)
 *  - expertReport_{reportId}_block_{blockId} — баллы за блоки
 *
 * Условия внутри одного элемента связываются через AND, элементы массива — через OR. Поэтому
 * фильтр по нескольким статусам разворачивается в несколько элементов одного и того же отчёта:
 * поле `status` на бэкенде — скаляр, массив статусов он не принимает.
 */
const buildConversation2ExpertReportFilters = (
  filters: TableFilterState,
): SearchConversationExpertReportFilter[] | undefined => {
  const byReportId = new Map<number, Partial<SearchConversationExpertReportFilter>>();
  const statusesByReportId = new Map<number, ExpertReportStatus[]>();

  for (const filter of filters) {
    const parsed = parseExpertReportFilterId(filter.id);
    if (!parsed || !filter.value) continue;

    const { reportId } = parsed;
    const v = filter.value as ExpertReportFilterValue;

    // Отбор по резюме без текста условия не ставит — и элемента заключения от него оставаться
    // не должно: сам по себе он сузил бы список до диалогов с результатом этого заключения
    if (parsed.type === 'field' && parsed.field === 'summary' && !toSummaryFilter(v)) continue;

    let entry = byReportId.get(reportId);
    if (!entry) {
      entry = { expertReport: { id: reportId } };
      byReportId.set(reportId, entry);
    }

    if (parsed.type === 'block') {
      // Результат-значение отбирается выбором из справочника блока, балл — диапазоном
      const blockValues = toValueList(v);
      const blockFilter: SearchConversationExpertReportBlockFilter | null = blockValues.length
        ? { blockId: parsed.blockId, values: blockValues }
        : isRatingRange(v)
          ? { blockId: parsed.blockId, score: { from: v.from, to: v.to } }
          : null;
      if (!blockFilter) continue;
      entry.blocks = [...(entry.blocks ?? []), blockFilter];
      continue;
    }

    const { field } = parsed;
    if (field === 'rating' && isRatingRange(v)) {
      entry.rating = { from: v.from, to: v.to };
    } else if (field === 'rating' && toValueList(v).length) {
      // Итог-значение балла не имеет: отбор по нему — перечисление подходящих значений
      entry.values = toValueList(v);
    } else if (field === 'summary') {
      entry.summary = toSummaryFilter(v);
    } else if (field === 'startedAt' && isDateRange(v)) {
      entry.startedAt = { from: v.from, to: v.to };
    } else if (field === 'finishedAt' && isDateRange(v)) {
      entry.finishedAt = { from: v.from, to: v.to };
    } else if (field === 'status') {
      const statuses = toStatusList(v);
      if (statuses.length) statusesByReportId.set(reportId, statuses);
    }
  }

  const result: SearchConversationExpertReportFilter[] = [];
  for (const [reportId, entry] of byReportId) {
    if (entry.expertReport == null) continue;
    const base = entry as SearchConversationExpertReportFilter;
    const statuses = statusesByReportId.get(reportId);
    if (!statuses?.length) {
      result.push(base);
      continue;
    }
    // Каждый статус — отдельный элемент с теми же остальными условиями отчёта (OR по статусам)
    statuses.forEach((status) => result.push({ ...base, status }));
  }
  return result.length > 0 ? result : undefined;
};

/**
 * Схлопывает колонки участников в объединённый состав `persons` — так их ждёт поиск:
 * ответ отдаёт всех участников с ролями, а колонки «Клиент» и «Консультант» разводятся
 * из него уже на экране.
 */
export const mapDialogsPageForConversationApi = (dialogsPage: SearchDialogsPage): SearchDialogsPage => {
  const { mainColumns, ...rest } = dialogsPage;
  if (!mainColumns?.length) return dialogsPage;

  const hasPersons = mainColumns.some((col) => (PERSONS_COLUMN_ALIASES as readonly string[]).includes(col));
  const withoutAliases = mainColumns.filter((col) => !(PERSONS_COLUMN_ALIASES as readonly string[]).includes(col));
  const mainColumnsForApi = hasPersons ? [...withoutAliases, 'persons'] : withoutAliases;

  return { ...rest, mainColumns: mainColumnsForApi as SearchDialogsPage['mainColumns'] };
};

/** Splits table filters into attributes.*, expertReport_*, and rest (spec: api/search/query.md) */
const splitConversationFilters = (filters: TableFilterState) => {
  const attributes: TableFilterState = filters
    .filter((filter) => filter.id.startsWith('attributes.'))
    .map(({ id, value }) => ({ id: id.replace('attributes.', ''), value }));
  const notAttributes: TableFilterState = filters.filter((filter) => !filter.id.startsWith('attributes.'));
  const expertReportFilters: TableFilterState = notAttributes.filter((f) => f.id.startsWith(EXPERT_REPORT_PREFIX));
  const restFilters: TableFilterState = notAttributes.filter((f) => !f.id.startsWith(EXPERT_REPORT_PREFIX));

  return { attributes, expertReportFilters, restFilters };
};

/**
 * Преобразует фильтры employeeId и client в формат persons (по спеке: filters.persons).
 * Возвращает новый объект, исходный не мутирует. Идентификаторы клиента и консультанта
 * объединяются в один массив persons.
 */
const mapEmployeeIdToPersons = (query: Record<string, unknown>): Record<string, unknown> => {
  const employeeIdFilter = query.employeeId;
  const clientFilter = query.client;
  if (employeeIdFilter === undefined && clientFilter === undefined) return query;

  const { employeeId: _emp, client: _cli, ...rest } = query;

  const toIds = (v: unknown): number[] => {
    if (v === undefined || v === null) return [];
    return Array.isArray(v) ? (v as number[]) : [v as number];
  };

  const ids = [...toIds(employeeIdFilter), ...toIds(clientFilter)];
  if (ids.length === 0) return rest;

  return {
    ...rest,
    persons: ids.map((id) => ({ id })) as SearchConversationPersonFilter[],
  };
};

/** Основные поля, поддерживающие прямую сортировку */
const MAIN_SORT_FIELDS = [
  'id',
  'externalId',
  'createdAt',
  'receivedAt',
  'processedAt',
  'duration',
  'relevance',
  'hasTranscription',
] as const;

/** Поля сортировки экспертного отчёта (без status — сортировка по статусу не поддерживается) */
const EXPERT_REPORT_SORT_FIELDS = ['rating', 'startedAt', 'finishedAt'] as const;
type ExpertReportSortField = (typeof EXPERT_REPORT_SORT_FIELDS)[number];

type ExpertReportSortParseResult =
  | { reportId: number; type: 'field'; field: ExpertReportSortField }
  | { reportId: number; type: 'block'; blockId: number };

/**
 * Парсит ID колонки сортировки экспертного отчёта.
 * Поддерживаемые форматы:
 *  - `expertReport_{reportId}_{field}` — стандартное поле (rating/startedAt/finishedAt)
 *  - `expertReport_{reportId}_block_{blockId}` — балл за блок
 */
const parseExpertReportSortId = (id: string): ExpertReportSortParseResult | null => {
  if (!id.startsWith(EXPERT_REPORT_PREFIX)) return null;
  const suffix = id.slice(EXPERT_REPORT_PREFIX.length);
  const parts = suffix.split('_');
  if (parts.length < 2) return null;
  const reportId = parseInt(parts[0], 10);
  if (Number.isNaN(reportId)) return null;
  if (parts[1] === 'block' && parts.length >= 3) {
    const blockId = parseInt(parts[2], 10);
    if (Number.isNaN(blockId)) return null;
    return { reportId, type: 'block', blockId };
  }
  if (EXPERT_REPORT_SORT_FIELDS.includes(parts[1] as ExpertReportSortField)) {
    return { reportId, type: 'field', field: parts[1] as ExpertReportSortField };
  }
  return null;
};

/**
 * Конвертирует TableSortingState в ConversationSort (спецификация: frontend/conversation/sorting.md).
 * Маппинг колонок:
 * - createdAt/receivedAt/processedAt → sort.{field}
 * - client/employeeId → sort.{field} (порядок по участнику выбранной роли)
 * - expertReport_{id}_{field} → sort.conversation2expertReport
 * - остальные → sort.attributes[path]
 */
export const buildConversationSort = (sorting: TableSortingState): ConversationSort | undefined => {
  if (!sorting?.length) return undefined;

  const result: ConversationSort = {};

  for (const item of sorting) {
    const direction: SortDirection = item.desc ? 'desc' : 'asc';
    const sortField: SortField = { sort: direction };

    // Основные поля с прямой сортировкой
    if ((MAIN_SORT_FIELDS as readonly string[]).includes(item.id)) {
      const key = item.id as (typeof MAIN_SORT_FIELDS)[number];
      result[key] = sortField;
      continue;
    }

    // Участники диалога: колонки разведены по ролям, каждая просит свой порядок.
    // Прежнее значение `persons` приходит из видов списка, сохранённых до разделения ролей,
    // и остаётся рабочим — сервер читает его как порядок по консультанту.
    if (item.id === 'client' || item.id === 'employeeId' || item.id === 'persons') {
      result[item.id] = sortField;
      continue;
    }

    // Экспертные отчёты
    if (item.id.startsWith(EXPERT_REPORT_PREFIX)) {
      const parsed = parseExpertReportSortId(item.id);
      if (parsed) {
        const expertReportSort: ConversationExpertReportSort =
          parsed.type === 'block'
            ? {
                expertReport: { id: parsed.reportId },
                block: { blockId: parsed.blockId, sort: direction },
              }
            : {
                expertReport: { id: parsed.reportId },
                [parsed.field]: sortField,
              };
        result.conversation2expertReport = [expertReportSort];
      }
      continue;
    }

    // Атрибуты — всё остальное. ID колонки приходит с префиксом attributes.
    // (см. useDialogDataGridColumns), а сервер ждёт ключ без префикса.
    if (!result.attributes) result.attributes = {};
    const attributeKey = item.id.startsWith('attributes.') ? item.id.slice('attributes.'.length) : item.id;
    result.attributes[attributeKey] = sortField;
  }

  return Object.keys(result).length > 0 ? result : undefined;
};

/** Builds filters for conversation entity (attributes, persons, dates, conversation2expertReport) */
export const buildConversationFilters = (
  filters: TableFilterState,
): SearchConversationRequest['filters'] | undefined => {
  const { attributes, expertReportFilters, restFilters } = splitConversationFilters(filters);
  const restQuery = tableFilters2Query(restFilters) as Record<string, unknown>;
  const restWithPersons = mapEmployeeIdToPersons(restQuery);

  const conversation2expertReport = buildConversation2ExpertReportFilters(expertReportFilters);

  const attributesQuery = tableFilters2Query(attributes);
  const hasAttributes = attributesQuery && Object.keys(attributesQuery).length > 0;
  const hasRest = restWithPersons && Object.keys(restWithPersons).length > 0;

  if (!hasAttributes && !hasRest && !conversation2expertReport) {
    return undefined;
  }

  const searchFilters: SearchConversationRequest['filters'] = {
    ...(hasAttributes && { attributes: attributesQuery }),
    ...restWithPersons,
    ...(conversation2expertReport && { conversation2expertReport }),
  };

  return searchFilters;
};
